import { useNavigate } from '@tanstack/react-router';
import moment from 'moment';
import { AnimatePresence, motion } from 'motion/react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useInView } from 'react-intersection-observer';
import { useSWRConfig } from 'swr';

import CrossIcon from '@common/assets/icons/cross.svg?react';
import HeartIcon from '@common/assets/icons/heart.svg?react';
import { useTabbar } from '@common/providers/useTabbar';
import { useAppStore } from '@common/stores/useAppStore';
import { BottomSheet } from '@common/ui/BottomSheet';
import { Button } from '@common/ui/buttons/Button';
import { IconButton } from '@common/ui/buttons/IconButton';
import { Disclaimer } from '@common/ui/Disclaimer';
import { Loader } from '@common/ui/Loader';
import { Result } from '@common/ui/Result';
import { useEventEmit } from '@common/utils/events';
import {
  getApiErrorInfo,
  getRetryAfterMs,
  isRateLimitedError,
  postEvent,
} from '@common/utils/fetcher';

import { useAccountStore } from '@entities/account';
import { useAuthStore } from '@entities/auth/stores/useAuthStore';
import { useIncomingSuperlikes } from '@entities/dating/hooks/useIncomingSuperlikes';
import {
  SUPERLIKE_HELD_NOTICE,
  useSuperlikeSend,
} from '@entities/dating/hooks/useSuperlikeSend';
import { useFiltersStore } from '@entities/dating/stores/useFiltersStore';
import {
  AdvertisingCard,
  AdvertisingCardSkeleton,
} from '@entities/dating/ui/AdvertisingCard';
import { BackButton } from '@entities/dating/ui/BackButton';
import { FeedActions } from '@entities/dating/ui/FeedActions';
import { FiltersButton } from '@entities/dating/ui/FiltersButton';
import { MatchDialog } from '@entities/dating/ui/MatchDialog';
import { ProfileCard } from '@entities/dating/ui/ProfileCard';
import { ProfileInfo } from '@entities/dating/ui/ProfileInfo';
import { useSuperlikeSessionStore } from '@entities/superlike/model';
import {
  FEED_ACTIONS_HEIGHT,
  isIncomingSuperlikeCard,
  superlikeCardMotions,
  superlikeCardMotionTarget,
  SuperlikeButton,
  SuperlikeLimitSheet,
  SuperlikeSendAnimation,
  SuperlikeSheet,
  usePrefersReducedMotion,
  useSuperlikeSendAnimation,
} from '@entities/superlike/ui';

import { ACCOUNT_ERROR_CODES } from '@services/AccountService';
import { analytics } from '@services/AnalyticsService';
import {
  FEED_PAGE_MAX_ENTRIES,
  FeedRequestSkipped,
  type FeedAdvertising,
  type FeedResponse,
} from '@services/FeedService';
import { feedAdQueryKey, useGetFeedAd } from '@services/hooks/FeedAdService';
import { getFeedQueryKey, useGetFeed } from '@services/hooks/FeedService';
import { usePostSwipe } from '@services/hooks/MatchesService';
import {
  type IncomingSuperlikeCard,
  type SuperlikeSendStatus,
} from '@services/SuperlikesService';
import { type User } from '@services/UsersService';

import { mergeFeedProfiles } from '@widgets/dating/utils/mergeFeedProfiles';
import { mergeIncomingSuperlikes } from '@widgets/dating/utils/mergeIncomingSuperlikes';
import {
  rememberSwipedId,
  withoutSwipedIds,
} from '@widgets/dating/utils/swipedIds';

const GENDERS = {
  male: 0,
  female: 1,
} as const;

const VISIBLE_CARDS_COUNT = 3;
// Automatic fetching (FEED-CORE 5.9 item 3, as refined in review). After a
// page that adds no new card, or says the feed has no more, the list fetches
// again on its own only after a swipe, a filter change, or a delay: 60 s,
// doubling with each such page in a row up to 15 minutes. A request that
// fails or brings no page is retried after 5 s, doubling up to 60 s. Timed
// retries run only while the page is visible; one that falls due while it is
// hidden runs when it is shown again.
const FEED_RETRY_AFTER_MS = 60 * 1000;
const FEED_RETRY_MAX_MS = 15 * 60 * 1000;
const FEED_FAILURE_RETRY_MS = 5 * 1000;
const FEED_FAILURE_RETRY_MAX_MS = 60 * 1000;

// A swipe refused by the request rate limits (errors.code RATE_LIMITED) is
// not the daily swipe limit: no paywall, a neutral notice, and no swipe until
// the server's Retry-After has passed (this long when it gives none).
const SWIPE_RATE_LIMIT_PAUSE_MS = 2 * 1000;
const SWIPE_RATE_LIMITED_NOTICE = {
  title: 'Слишком много действий',
  description: 'Попробуйте ещё раз чуть позже',
};

// The first feed page waits this long at most for the received superlikes;
// later ones go in front of the deck when they come.
export const INCOMING_SUPERLIKES_WAIT_MS = 2000;
// The card and the tab bar's space under it (today's layout).
const FEED_CARD_HEIGHT = 'calc(100vh - 66px - 8px)';
// With the superlike the same space holds a shorter card and its button row.
const CARD_HEIGHT_OVER_ACTIONS = `calc(100% - ${FEED_ACTIONS_HEIGHT}px)`;

const isNotDatingError = (error: unknown) =>
  getApiErrorInfo(error).code === ACCOUNT_ERROR_CODES.notDating;

const doublingDelay = (baseMs: number, maxMs: number, inARow: number) =>
  Math.min(baseMs * 2 ** Math.max(0, inARow - 1), maxMs);

/**
 * The next page's `limit` (FEED-CORE 5.6): the room left in the deck,
 * counted in entries with ad placeholders, as the server counts them. A free
 * user keeps `remaining` entries in all, a premium user (`remaining` null)
 * 70. Below 1 there is nothing to fetch. Without a swipe status (undefined)
 * the list fetches nothing, as before.
 */
const feedRequestLimit = (
  remaining: number | null | undefined,
  held: number,
) => {
  if (remaining === undefined) return 0;

  return Math.min(
    (remaining ?? FEED_PAGE_MAX_ENTRIES) - held,
    FEED_PAGE_MAX_ENTRIES,
  );
};

const feedSWRRuntimeOptions = {
  revalidateOnMount: false,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  revalidateIfStale: false,
  // The list reads only what its own requests return, so SWR's own retries
  // of a failed request would be wasted; the list retries itself.
  shouldRetryOnError: false,
};
const feedAdSWRRuntimeOptions = {
  revalidateOnMount: true,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  revalidateIfStale: false,
  dedupingInterval: Infinity,
};

export type ProfilesListProps = {
  /**
   * Height taken above the list (DatingHome's mode control), so the card
   * still ends above the tab bar. Without it, today's layout.
   */
  topOffset?: number;
};

export const ProfilesList: React.FC<ProfilesListProps> = ({ topOffset }) => {
  const tabbar = useTabbar();
  const navigate = useNavigate();
  const emit = useEventEmit();
  const auth = useAuthStore();
  const { cache } = useSWRConfig();

  const [ref, isInView] = useInView({
    rootMargin: '0px 0px -100px 0px',
  });
  // The buttons under the photo: the tab bar's take over once these scroll away.
  const [feedActionsRef, isFeedActionsInView] = useInView();

  const { filters, getFiltersCount } = useFiltersStore();

  const { tasks: responseTasks } = useAppStore();

  // Superlikes, only with the feature: without it the list is today's.
  const isSuperlikeEnabled = useAccountStore(
    (state) => state.account?.features.superlike === true,
  );
  const rememberAnsweredSender = useSuperlikeSessionStore(
    (state) => state.rememberAnsweredSender,
  );
  const { cards: incomingSuperlikes, isSettled: isIncomingSettled } =
    useIncomingSuperlikes({ isEnabled: isSuperlikeEnabled });
  const isReducedMotion = usePrefersReducedMotion();
  const superlikeAnimation = useSuperlikeSendAnimation();
  // Received superlikes waiting to go in front of the deck: never while a
  // feed request is in flight (its page's cut would move), while a sheet is
  // open, while the send animation plays or while the top card is dragged
  // (the deck must not reorder under the user).
  const pendingIncomingRef = useRef<IncomingSuperlikeCard[] | null>(null);
  const isSuperlikeSheetOpenRef = useRef(false);
  const isTopCardDraggedRef = useRef(false);
  // The first feed page waits for the received superlikes to be in the deck,
  // so that its `limit` counts them (they cost a swipe each), but not for
  // longer than INCOMING_SUPERLIKES_WAIT_MS.
  const [hasMergedIncoming, setHasMergedIncoming] = useState(false);
  const [isIncomingWaitOver, setIsIncomingWaitOver] = useState(false);
  const isDeckReadyForFeed =
    !isSuperlikeEnabled ||
    hasMergedIncoming ||
    isIncomingWaitOver ||
    (isIncomingSettled && incomingSuperlikes === undefined);
  const applyPendingIncomingRef = useRef(() => undefined as void);
  const feedQueryParams = {
    looking_for:
      auth?.user?.sex === GENDERS.female ? GENDERS.male : GENDERS.female,
    age_from: filters.age?.from || undefined,
    age_to: filters.age?.to || undefined,
    has_about: filters.hasAbout || undefined,
    is_verified: filters.isVerified || undefined,
    goal: filters.goals || undefined,
    alcohol: filters.alcoholViews || undefined,
    smoking: filters.smokingViews || undefined,
    children: filters.hasChildren || undefined,
    zodiac: filters.zodiac || undefined,
    height_from: filters.height?.from || undefined,
    height_to: filters.height?.to || undefined,
  };
  // The request's identity: it changes when, and only when, a filter does.
  const feedKey = getFeedQueryKey(feedQueryParams);
  // SWR's mutate resolves with its cache when a request fails, so the list
  // learns of a failure from onError: the key of the last failed request.
  const failedFeedKeyRef = useRef<string | null>(null);
  const failedFeedErrorRef = useRef<unknown>(null);
  const feedSWROptions = useMemo(
    () => ({
      ...feedSWRRuntimeOptions,
      onError: (error: unknown, key: string) => {
        // A request SWR started on its own, skipped without a request (see
        // readFeedLimit). It says nothing about the list's own request, and
        // can settle after the list cleared this ref for that request.
        if (error instanceof FeedRequestSkipped) return;
        failedFeedKeyRef.current = key;
        failedFeedErrorRef.current = error;
      },
    }),
    [],
  );

  const { trigger: triggerPostSwipe } = usePostSwipe();

  const [profiles, setProfiles] = useState<FeedResponse[]>([]);
  const [skippedProfile, setSkippedProfile] = useState<User>();
  const [isShowDisclaimer, setIsShowDisclaimer] = useState(false);
  const [isShowMatchDialog, setIsShowMatchDialog] = useState(false);
  const [isShowNoMoreSwipeError, setIsShowNoMoreSwipeError] = useState(false);
  // True while swipes wait out a rate limit's Retry-After. The ref is read by
  // the bottom sheet's buttons, whose handlers are set by an effect.
  const [isSwipePaused, setIsSwipePaused] = useState(false);
  const isSwipePausedRef = useRef(false);
  const swipePauseTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const [matchProfile, setMatchProfile] = useState<User>();
  // The match dialog's first text: empty after a superlike's match.
  const [matchInitialMessage, setMatchInitialMessage] = useState<string>();
  // GET /feed or a superlike answered 403 not_dating: dating is off.
  const [isNotDating, setIsNotDating] = useState(false);
  // The card a superlike was just sent to, while its animation plays.
  const [superlikeSentId, setSuperlikeSentId] = useState<string | null>(null);
  // The card a feed button sent away, while it leaves: one at a time.
  const [flyOut, setFlyOut] = useState<{
    id: string;
    action: 'like' | 'pass';
  } | null>(null);
  const isFlyingOutRef = useRef(false);
  const [isFetched, setIsFetched] = useState(false);
  // The feed key that automatic fetching is stopped for, or null. A filter
  // change makes it another key, so the stop no longer applies.
  const [stoppedFeedKey, setStoppedFeedKey] = useState<string | null>(null);
  // Bumped to run the fetch effect again, for a page dropped because the
  // filters changed while it was requested.
  const [refetchToken, setRefetchToken] = useState(0);
  // The cards swiped while the list is mounted, bounded; an undo or a failed
  // swipe forgets its card. Every incoming page drops them.
  const [swipedIds] = useState(() => new Set<string>());
  const isMountedRef = useRef(false);
  const isFetchingFeedRef = useRef(false);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const isRetryDueRef = useRef(false);
  const failuresInARowRef = useRef(0);
  const idlePagesInARowRef = useRef(0);
  const latestFeedKeyRef = useRef(feedKey);
  const profilesRef = useRef(profiles);
  const remainingRef = useRef(auth?.swipe_status?.remaining);
  // True only while loadMoreProfiles starts its request.
  const isListRequestRef = useRef(false);

  useLayoutEffect(() => {
    profilesRef.current = profiles;
  }, [profiles]);

  useLayoutEffect(() => {
    remainingRef.current = auth?.swipe_status?.remaining;
  }, [auth?.swipe_status?.remaining]);

  useLayoutEffect(() => {
    latestFeedKeyRef.current = feedKey;
  }, [feedKey]);

  // The page's `limit`, read by the fetcher when a request starts. It is not
  // a query param: a swipe changes it, and must not change the SWR key.
  // Only the list's own requests get one. SWR also fetches a new key on its
  // own (on a filter change); that request gets 0, so getFeed skips it
  // without a request: nobody would merge its page, yet the server would
  // record it as served.
  const readFeedLimit = useCallback(
    () =>
      isListRequestRef.current
        ? feedRequestLimit(remainingRef.current, profilesRef.current.length)
        : 0,
    [],
  );
  const {
    mutate: mutateFeed,
    isLoading: isLoadingFeed,
    isValidating: isValidatingFeed,
  } = useGetFeed({
    queryParams: feedQueryParams,
    getLimit: readFeedLimit,
    swrOptions: feedSWROptions,
  });

  const clearRetry = useCallback(() => {
    clearTimeout(retryTimerRef.current);
    retryTimerRef.current = undefined;
    isRetryDueRef.current = false;
  }, []);

  const stopAutoFetch = useCallback(
    (key: string, retryAfterMs: number) => {
      clearRetry();
      setStoppedFeedKey(key);
      retryTimerRef.current = setTimeout(() => {
        retryTimerRef.current = undefined;
        if (document.visibilityState !== 'visible') {
          isRetryDueRef.current = true;
          return;
        }
        setStoppedFeedKey(null);
      }, retryAfterMs);
    },
    [clearRetry],
  );

  const resumeAutoFetch = useCallback(() => {
    clearRetry();
    failuresInARowRef.current = 0;
    idlePagesInARowRef.current = 0;
    setStoppedFeedKey(null);
  }, [clearRetry]);

  useEffect(() => {
    isMountedRef.current = true;
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isRetryDueRef.current) {
        isRetryDueRef.current = false;
        setStoppedFeedKey(null);
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      isMountedRef.current = false;
      clearTimeout(retryTimerRef.current);
      clearTimeout(swipePauseTimerRef.current);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  // A filter change lifts any stop and starts the back-off over.
  useEffect(() => {
    clearRetry();
    failuresInARowRef.current = 0;
    idlePagesInARowRef.current = 0;
    setStoppedFeedKey(null);
  }, [clearRetry, feedKey]);

  const isAutoFetchStopped = stoppedFeedKey === feedKey;

  const activeTasksCount = useMemo(() => {
    return (responseTasks || []).filter((task) => task.status === 'available')
      ?.length;
  }, [responseTasks]);

  const currentButtons = useMemo(() => {
    if (auth?.user?.is_premium && activeTasksCount === 0) {
      return null;
    }

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          width: '100%',
        }}
      >
        {activeTasksCount > 0 && (
          <Button
            testId="profiles-list-tasks-button"
            variant="premiumGradient"
            size={1000}
            onClick={() => navigate({ to: '/tasks' })}
          >
            Выполнить задания
          </Button>
        )}

        {!auth?.user?.is_premium && (
          <Button
            testId="profiles-list-premium-button"
            variant={
              activeTasksCount > 0 ? 'actionSecondary' : 'premiumGradient'
            }
            size={1000}
            onClick={() => navigate({ to: '/premium' })}
          >
            Подключить Premium
          </Button>
        )}
      </div>
    );
  }, [activeTasksCount, auth?.user]);

  const emptyResultDescriptionText = useMemo(() => {
    if (!auth?.user?.is_premium && activeTasksCount === 0) {
      return 'А пока подключи Premium, чтобы свайпать без ограничений';
    }

    if (!auth?.user?.is_premium && activeTasksCount > 0) {
      return 'А пока выполни задания или подключи Premium';
    }

    if (auth?.user?.is_premium && activeTasksCount === 0) {
      return 'Если у вас уже есть мэтч, то напишите ему скорее';
    }

    if (auth?.user?.is_premium && activeTasksCount > 0) {
      return 'А пока выполните задания, чтобы продлить Premium';
    }
  }, [activeTasksCount, auth?.user]);

  const hasSwipes =
    auth?.swipe_status?.remaining === null || auth?.swipe_status?.remaining > 0;

  const loadMoreProfiles = useCallback(async () => {
    // One request at a time: SWR's mutate starts a new request even while
    // one is in flight.
    if (isFetchingFeedRef.current) {
      return;
    }
    // The deck now, and so the page's `limit`: mutateFeed calls the fetcher
    // synchronously, and readFeedLimit reads these same refs.
    const remaining = remainingRef.current;
    const held = profilesRef.current.length;
    const limit = feedRequestLimit(remaining, held);
    // No room: nothing to fetch, and nothing armed. The fetch effect runs
    // again when the deck shrinks or the remaining swipes grow.
    if (limit < 1) {
      return;
    }
    // Where the deck is cut. Free: at `remaining`, as before. Premium: at
    // held + limit, not at the page's length, so that every card the server
    // recorded for this page is kept (M1).
    // Known gap: an undo while the request is in flight (premium, or a free
    // user through the top BackButton) puts one card back in the deck, so a
    // full page loses its last card to the cut: 1 card recorded as served
    // and never shown (accepted, PR5-slices section 14).
    const sliceEnd = remaining ?? held + limit;
    isFetchingFeedRef.current = true;
    const requestFeedKey = feedKey;
    // A failed or missing page counts as no progress, retried sooner.
    const retryAfterFailure = () => {
      failuresInARowRef.current += 1;
      stopAutoFetch(
        requestFeedKey,
        doublingDelay(
          FEED_FAILURE_RETRY_MS,
          FEED_FAILURE_RETRY_MAX_MS,
          failuresInARowRef.current,
        ),
      );
    };
    // The filters changed while the page was requested: it is dropped, and
    // the fetch effect runs again for the new filters.
    const isStale = () => requestFeedKey !== latestFeedKeyRef.current;

    try {
      const requestedAt = performance.now();
      failedFeedKeyRef.current = null;
      failedFeedErrorRef.current = null;
      // The only window in which readFeedLimit gives the fetcher a limit.
      isListRequestRef.current = true;
      const pending = mutateFeed();
      isListRequestRef.current = false;
      const feed = await pending;
      if (!isMountedRef.current) return;
      if (isStale()) {
        setRefetchToken((token) => token + 1);
        return;
      }
      if (!feed || failedFeedKeyRef.current === requestFeedKey) {
        if (isNotDatingError(failedFeedErrorRef.current)) {
          setIsNotDating(true);
          return;
        }
        retryAfterFailure();
        return;
      }
      failuresInARowRef.current = 0;
      const loadTime = Math.round(performance.now() - requestedAt);

      setIsFetched(true);

      const resultCount = feed.results?.length || 0;
      analytics.trackFeedLoaded(resultCount, loadTime);

      const incoming = withoutSwipedIds(feed.results ?? [], swipedIds);
      const held = profilesRef.current;
      const heldIds = new Set(
        held.flatMap((item) => ('id' in item ? [item.id] : [])),
      );
      // Progress: the merge keeps a card the deck did not hold.
      const addsNewCard = mergeFeedProfiles(held, incoming, sliceEnd).some(
        (item) => 'id' in item && !heldIds.has(item.id),
      );

      setProfiles((prev) => mergeFeedProfiles(prev, incoming, sliceEnd));

      idlePagesInARowRef.current = addsNewCard
        ? 0
        : idlePagesInARowRef.current + 1;
      if (!addsNewCard || feed.hasMore === false) {
        stopAutoFetch(
          requestFeedKey,
          doublingDelay(
            FEED_RETRY_AFTER_MS,
            FEED_RETRY_MAX_MS,
            idlePagesInARowRef.current,
          ),
        );
      }
    } catch (error) {
      if (!isMountedRef.current) return;
      if (isStale()) {
        setRefetchToken((token) => token + 1);
        return;
      }
      if (isNotDatingError(error)) {
        setIsNotDating(true);
        return;
      }
      emit('error', {
        title: 'Ошибка загрузки анкет',
        description: 'Попробуйте позже',
      });
      retryAfterFailure();
    } finally {
      isFetchingFeedRef.current = false;
      applyPendingIncomingRef.current();
    }
  }, [emit, feedKey, mutateFeed, stopAutoFetch, swipedIds]);

  useEffect(() => {
    if (isDeckReadyForFeed) return;

    const timer = setTimeout(
      () => setIsIncomingWaitOver(true),
      INCOMING_SUPERLIKES_WAIT_MS,
    );

    return () => clearTimeout(timer);
  }, [isDeckReadyForFeed]);

  // A short deck refills. loadMoreProfiles fetches only while the deck has
  // room for another entry (free: a remaining swipe beyond the entries held),
  // so this runs again when the deck shrinks or the swipe status changes.
  useEffect(() => {
    // Dating is off: nothing to fetch, and no retry toasts.
    if (isAutoFetchStopped || isNotDating) {
      return;
    }
    // The received superlikes come first: the first page waits for them.
    if (!isDeckReadyForFeed) {
      return;
    }

    const profilesLength = profiles?.length || 0;

    if (!!auth?.user && profilesLength <= 5) {
      void loadMoreProfiles();
    }
  }, [
    auth?.user,
    auth?.swipe_status,
    isAutoFetchStopped,
    isDeckReadyForFeed,
    isNotDating,
    loadMoreProfiles,
    profiles?.length,
    refetchToken,
  ]);

  // A playing animation or a paused swipe (which only repeats the notice):
  // the buttons, the heart and the send wait. Dragging a card is off then too.
  const isCardInputBlocked = () => {
    if (superlikeAnimation.isPlayingRef.current || isFlyingOutRef.current) {
      return true;
    }
    if (!isSwipePausedRef.current) return false;

    emit('info', SWIPE_RATE_LIMITED_NOTICE);

    return true;
  };

  const superlike = useSuperlikeSend({
    isBlocked: isCardInputBlocked,
    onSent: (profile, response) => handleSuperlikeSent(profile, response),
    onDropped: (profile) => dropProfile(profile),
    onSwipeLimit: () => showSwipeLimit(false),
    onRateLimited: (error) => {
      pauseSwipes(getRetryAfterMs(error) ?? SWIPE_RATE_LIMIT_PAUSE_MS);
      emit('info', SWIPE_RATE_LIMITED_NOTICE);
    },
    onNotDating: () => setIsNotDating(true),
  });
  const isSuperlikeSheetOpen = superlike.isSheetOpen;

  useLayoutEffect(() => {
    isSuperlikeSheetOpenRef.current = isSuperlikeSheetOpen;
  }, [isSuperlikeSheetOpen]);

  // The tab bar's single slot is a pure function of the list's state: empty
  // while a superlike sheet is open (its buttons are in the sheet) and while
  // dating is off.
  useEffect(() => {
    if (
      !isSuperlikeSheetOpen &&
      !isNotDating &&
      profiles[0] &&
      !('ad' in profiles[0])
    ) {
      const actionUser = profiles[0];

      tabbar.setBottomSheet(
        isInView && !isFeedActionsInView ? (
          <BottomSheet
            buttons={[
              <IconButton
                testId="profiles-list-pass-button"
                size={500}
                variant="negativeSecondary"
                onClick={() => handleSwipeButtonClick('pass', actionUser)}
              >
                <CrossIcon />
              </IconButton>,
              <IconButton
                testId="profiles-list-like-button"
                size={500}
                variant="actionSecondary"
                onClick={() => handleSwipeButtonClick('like', actionUser)}
              >
                <HeartIcon />
              </IconButton>,
            ]}
            buttonDirection="row"
          />
        ) : undefined,
      );
    } else {
      tabbar.setBottomSheet(undefined);
    }
  }, [
    isFeedActionsInView,
    isInView,
    isNotDating,
    isSuperlikeSheetOpen,
    profiles,
  ]);

  // The slot outlives the list (it resets only on a route change): clear it,
  // or a mode switch on the same route leaves live buttons behind.
  const setBottomSheetRef = useRef(tabbar.setBottomSheet);

  useLayoutEffect(() => {
    setBottomSheetRef.current = tabbar.setBottomSheet;
  });

  useEffect(() => () => setBottomSheetRef.current(undefined), []);

  useEffect(() => {
    const root = document.getElementById('root');
    if (!root) return;

    root.style.overscrollBehavior = 'none';

    return () => {
      root.style.overscrollBehavior = 'auto';
    };
  }, []);

  const handleSkipAdvertising = () => {
    setProfiles((prev) => (prev ?? []).filter((_, index) => index !== 0));

    cache.delete(feedAdQueryKey);
  };

  /**
   * The daily swipe limit: a toast, «Свайпы закончились» and the paywall
   * events. `isSwipeTaken`: a swipe of this list already took one from the
   * count (a superlike takes none before its answer).
   */
  const showSwipeLimit = useCallback(
    (
      isSwipeTaken: boolean,
      swipeStatus = useAuthStore.getState().swipe_status,
    ) => {
      const timeLeft = moment(swipeStatus?.reset_time).fromNow(true);

      emit('error', {
        title: 'Лимит свайпов исчерпан',
        description: `До обновления свайпов осталось ${timeLeft}`,
      });

      setIsShowNoMoreSwipeError(true);

      const limitVal = swipeStatus?.limit;
      const currRemaining = swipeStatus?.remaining;
      const usedSwipes =
        typeof limitVal === 'number' && typeof currRemaining === 'number'
          ? limitVal - (currRemaining + (isSwipeTaken ? 1 : 0))
          : null;
      postEvent(
        'first_paywall_impression',
        {
          source: 'swipe_limit_reached',
          swipes_until_paywall: usedSwipes,
          minutes_since_registration: null,
        },
        true,
      );
      postEvent('paywall_cycle_hit', {
        source: 'swipe_limit_reached',
        swipes_until_paywall: usedSwipes,
        minutes_since_registration: null,
      });
    },
    [emit],
  );

  const pauseSwipes = useCallback((pauseMs: number) => {
    // A refusal that lands after the list unmounted pauses nothing.
    if (!isMountedRef.current) return;

    clearTimeout(swipePauseTimerRef.current);
    isSwipePausedRef.current = true;
    setIsSwipePaused(true);
    swipePauseTimerRef.current = setTimeout(() => {
      isSwipePausedRef.current = false;
      setIsSwipePaused(false);
    }, pauseMs);
  }, []);

  const handleProfileAction = useCallback(
    async (action: 'like' | 'pass', profile: User) => {
      const profileId = profile.id;

      document.querySelector('#root')?.scrollTo({ top: 0, behavior: 'smooth' });
      setIsShowDisclaimer(false);
      rememberSwipedId(swipedIds, profileId);
      // An answered superlike stays answered for the session, here and in
      // the Likes tab, though the server may not list the swipe yet.
      if (isIncomingSuperlikeCard(profile)) {
        rememberAnsweredSender(profileId);
      }
      // A swipe changes what the feed returns: the fetch effect may refill
      // the deck again.
      resumeAutoFetch();

      if (action === 'like') {
        setProfiles((prev) =>
          (prev ?? []).filter(
            (p): p is FeedResponse => 'ad' in p || p.id !== profileId,
          ),
        );
        analytics.trackSwipeLike(0);

        if (profile?.liked_me) {
          setIsShowMatchDialog(true);
          setMatchProfile(profile);
          setMatchInitialMessage(undefined);
        }
      } else {
        analytics.trackSwipePass(0);
        setProfiles((prev) =>
          (prev ?? []).filter(
            (p): p is FeedResponse => 'ad' in p || p.id !== profileId,
          ),
        );
        setSkippedProfile(profile);

        if (profile?.liked_me) {
          setIsShowDisclaimer(true);
        }
      }

      // Whether this swipe was taken from the count. The count is read from
      // the store, not from `auth`: the bottom sheet's buttons keep the
      // handler of the render that last set them, so `auth` can be older
      // than a give-back below.
      let isSwipeTaken = false;

      try {
        const { swipe_status: swipeStatus, setSwipeStatus } =
          useAuthStore.getState();

        if (swipeStatus && swipeStatus.remaining !== null) {
          setSwipeStatus({
            ...swipeStatus,
            remaining: (swipeStatus.remaining ?? 0) - 1,
          });
          isSwipeTaken = true;
        }
        await triggerPostSwipe({
          action,
          target_user_id: profile.id,
        });
      } catch (e) {
        // The server did not record the swipe: a later page may serve the
        // card again.
        swipedIds.delete(String(profileId));

        if (isRateLimitedError(e)) {
          // Refused before it was counted: give back the swipe taken above,
          // or a last one would read as the daily limit.
          const { swipe_status: swipeStatus, setSwipeStatus } =
            useAuthStore.getState();

          if (isSwipeTaken && typeof swipeStatus?.remaining === 'number') {
            setSwipeStatus({
              ...swipeStatus,
              remaining: swipeStatus.remaining + 1,
            });
          }

          pauseSwipes(getRetryAfterMs(e) ?? SWIPE_RATE_LIMIT_PAUSE_MS);
          emit('info', SWIPE_RATE_LIMITED_NOTICE);

          return;
        }

        const error = e as { message: string };

        if (error?.message?.includes('429')) {
          showSwipeLimit(true, auth?.swipe_status);

          return;
        }

        emit('error', {
          title: 'Ошибка действия',
          description: 'Попробуйте позже',
        });
      }
    },
    [
      auth,
      emit,
      pauseSwipes,
      rememberAnsweredSender,
      resumeAutoFetch,
      showSwipeLimit,
      swipedIds,
      triggerPostSwipe,
    ],
  );

  const handleSwipeButtonClick = (action: 'like' | 'pass', profile: User) => {
    if (isCardInputBlocked()) return;

    handleProfileAction(action, profile);
  };

  // A button under the photo: the card plays its wash and leaves first, then
  // the action runs as after a swipe. Without motion it just runs.
  const handleFeedActionClick = (action: 'like' | 'pass', profile: User) => {
    if (isCardInputBlocked()) return;

    if (isReducedMotion) {
      handleProfileAction(action, profile);

      return;
    }

    isFlyingOutRef.current = true;
    setFlyOut({ id: profile.id, action });
  };

  const handleCardAnswered = (action: 'like' | 'pass', profile: User) => {
    isFlyingOutRef.current = false;
    setFlyOut(null);
    handleProfileAction(action, profile);
  };

  const handleReturnSkippedProfile = () => {
    if (superlikeAnimation.isPlayingRef.current) return;

    if (skippedProfile) {
      swipedIds.delete(String(skippedProfile.id));
      setProfiles([skippedProfile, ...(profiles ?? [])]);
      setSkippedProfile(undefined);
      setIsShowDisclaimer(false);
    }
  };

  /** Takes a card out of the deck for good, without a swipe of its own. */
  const dropProfile = (profile: User) => {
    rememberSwipedId(swipedIds, profile.id);
    setProfiles((prev) =>
      (prev ?? []).filter(
        (p): p is FeedResponse => 'ad' in p || p.id !== profile.id,
      ),
    );
  };

  /** One swipe from a non-Premium user's count (Premium has none). */
  const takeSwipe = () => {
    const { swipe_status: swipeStatus, setSwipeStatus } =
      useAuthStore.getState();

    if (swipeStatus && swipeStatus.remaining !== null) {
      setSwipeStatus({
        ...swipeStatus,
        remaining: (swipeStatus.remaining ?? 0) - 1,
      });
    }
  };

  /**
   * A superlike's card leaves like a liked one: out of the deck, one swipe
   * taken for a non-Premium user, the deck free to refill.
   */
  const removeSuperlikedProfile = (profile: User) => {
    document.querySelector('#root')?.scrollTo({ top: 0, behavior: 'smooth' });
    setIsShowDisclaimer(false);
    dropProfile(profile);
    resumeAutoFetch();
    takeSwipe();
  };

  const finishSuperlike = (profile: User, status: SuperlikeSendStatus) => {
    setSuperlikeSentId(null);
    removeSuperlikedProfile(profile);

    if (status === 'held') {
      emit('info', SUPERLIKE_HELD_NOTICE);
    }

    if (status === 'matched') {
      setMatchProfile(profile);
      setMatchInitialMessage('');
      setIsShowMatchDialog(true);
    }

    applyPendingIncomingRef.current();
  };

  // 200: the sheet is closed; the animation plays on the card, then it goes.
  // A match ends after the burst, with the match dialog.
  const handleSuperlikeSent = (
    profile: User,
    { status }: { status: SuperlikeSendStatus },
  ) => {
    // The answer came after the list left: no animation or toast on the next
    // screen; only the swipe it took is counted.
    if (!isMountedRef.current) {
      takeSwipe();

      return;
    }

    setSuperlikeSentId(profile.id);
    superlikeAnimation.play({
      isReducedMotion,
      isEndingAfterBurst: status === 'matched',
      onDone: () => finishSuperlike(profile, status),
    });
  };

  const applyPendingIncoming = () => {
    const incoming = pendingIncomingRef.current;

    if (
      !incoming ||
      isFetchingFeedRef.current ||
      superlikeAnimation.isPlayingRef.current ||
      isSuperlikeSheetOpenRef.current ||
      isTopCardDraggedRef.current
    ) {
      return;
    }

    pendingIncomingRef.current = null;

    const fresh = incoming.filter((card) => !swipedIds.has(String(card.id)));
    const { added } = mergeIncomingSuperlikes(profilesRef.current, fresh);

    if (added === 0) return;

    // A free user's deck holds `remaining` entries at most, as a feed page's
    // cut keeps it: a received superlike costs a swipe too, so the tail
    // gives way.
    const remaining = remainingRef.current;
    const withinLimit = (deck: FeedResponse[]) =>
      typeof remaining === 'number' ? deck.slice(0, remaining) : deck;

    setProfiles((prev) =>
      withinLimit(mergeIncomingSuperlikes(prev, fresh).deck),
    );
    analytics.trackSuperlikeIncomingShown(added);
  };

  // The top card is dragged: the deck waits; a pending prepend lands after.
  const handleTopCardDrag = (isDragged: boolean) => {
    isTopCardDraggedRef.current = isDragged;

    if (!isDragged) {
      applyPendingIncomingRef.current();
    }
  };

  useLayoutEffect(() => {
    applyPendingIncomingRef.current = applyPendingIncoming;
  });

  // Each answer of the received superlikes goes in front of the deck, now
  // or as soon as nothing holds the deck still.
  useEffect(() => {
    if (!incomingSuperlikes) return;

    pendingIncomingRef.current = incomingSuperlikes;
    applyPendingIncomingRef.current();
    setHasMergedIncoming(true);
  }, [incomingSuperlikes]);

  useEffect(() => {
    if (!isSuperlikeSheetOpen) {
      applyPendingIncomingRef.current();
    }
  }, [isSuperlikeSheetOpen]);

  const visibleProfiles = useMemo(() => {
    return (profiles ?? []).slice(0, VISIBLE_CARDS_COUNT);
  }, [profiles]);

  const isEnabledFeedAd = useMemo(
    () => visibleProfiles.some((item) => (item as FeedAdvertising)?.ad),
    [visibleProfiles],
  );

  const { data: dataFeedAd, isLoading: isLoadingFeedAd } = useGetFeedAd({
    enabled: isEnabledFeedAd,
    swrOptions: feedAdSWRRuntimeOptions,
  });

  const isLoadingProfiles =
    isLoadingFeed || isValidatingFeed || !isDeckReadyForFeed;

  // The card a superlike was sent to, and the one under it, while the
  // animation plays.
  const superlikeSentIndex =
    superlikeSentId === null
      ? -1
      : visibleProfiles.findIndex(
          (item) => 'id' in item && item.id === superlikeSentId,
        );
  // The superlike button: with the feature, on a plain feed card; not on a
  // received superlike or like (a like back is the match), not on ads.
  const hasSuperlikeHeart = (profile: User) =>
    superlike.isEnabled &&
    !isIncomingSuperlikeCard(profile) &&
    !profile.liked_me;

  const superlikeSendFor = (index: number) => {
    if (superlikeSentIndex < 0) return undefined;
    if (index === superlikeSentIndex) {
      return { phase: superlikeAnimation.phase, role: 'sent' as const };
    }
    if (index === superlikeSentIndex + 1) {
      return { phase: superlikeAnimation.phase, role: 'next' as const };
    }

    return undefined;
  };

  const isLoadingFinishedAndEmptyProfiles =
    !isLoadingProfiles && (profiles?.length ?? 0) === 0;

  // The superlike animation's flight, for its effects over the card and row.
  const superlikeFxMotion =
    superlikeSentIndex >= 0
      ? superlikeCardMotions(superlikeAnimation.phase).sent
      : undefined;

  const isNoMoreSwipes =
    (isLoadingFinishedAndEmptyProfiles && !hasSwipes) || isShowNoMoreSwipeError;

  // The card the buttons under the photo answer: none over an ad.
  const topCard = visibleProfiles[0];
  const feedActionsCard =
    isSuperlikeEnabled && !isNoMoreSwipes && topCard && !('ad' in topCard)
      ? topCard
      : null;

  const isEmptyResult =
    isLoadingFinishedAndEmptyProfiles &&
    isFetched &&
    getFiltersCount() === 0 &&
    hasSwipes;

  const isEmptyFilterResult =
    isLoadingFinishedAndEmptyProfiles &&
    isFetched &&
    getFiltersCount() > 0 &&
    hasSwipes;

  // Track empty feed states
  useEffect(() => {
    if (isEmptyResult) {
      analytics.trackFeedEmpty(false); // No filters applied
    } else if (isEmptyFilterResult) {
      analytics.trackFeedEmpty(true); // Filters applied but no results
    }
  }, [isEmptyResult, isEmptyFilterResult]);

  const swipeLeftTime = moment(auth?.swipe_status?.reset_time).fromNow(true);

  useEffect(() => {
    const hasAd = profiles.some((item) => 'ad' in item);

    if (isEnabledFeedAd && !isLoadingFeedAd && !dataFeedAd && hasAd) {
      setProfiles((prev) => (prev ?? []).filter((item) => !('ad' in item)));
    }
  }, [isEnabledFeedAd, isLoadingFeedAd, dataFeedAd, profiles]);

  return (
    <>
      {!isShowMatchDialog && (
        <>
          <div
            data-test-id="profiles-list-container"
            style={{
              position: 'relative',
              height: topOffset
                ? `calc(100vh - 66px - 8px - ${topOffset}px)`
                : FEED_CARD_HEIGHT,
              overflow: 'hidden',
            }}
          >
            {isLoadingProfiles && (
              <Loader
                style={{
                  zIndex: 1,
                }}
              />
            )}

            {isNotDating ? (
              <Result
                src={'/icons/3d-icons/like.png'}
                title="Вы сейчас не знакомитесь"
                style={{
                  zIndex: 1,
                }}
                iconContainerBg="var(--bg-action-secondary-transparent)"
                button={
                  <Button
                    testId="profiles-list-enable-dating-button"
                    variant="actionPrimary"
                    size={1000}
                    onClick={() => navigate({ to: '/profile/settings' })}
                  >
                    Включить знакомства
                  </Button>
                }
              />
            ) : (
              <>
                {isEmptyResult && (
                  <Result
                    src={'/icons/3d-icons/sand-clock.png'}
                    title="Ждём новые анкеты"
                    description={emptyResultDescriptionText}
                    style={{
                      zIndex: 1,
                    }}
                    button={currentButtons}
                    iconContainerBg="rgba(255, 92, 140, 0.07)"
                  />
                )}

                {isEmptyFilterResult && (
                  <Result
                    src={'/icons/3d-icons/sand-clock.png'}
                    title="Не нашли анкеты"
                    description="Попробуйте изменить или сбросить фильтры"
                    style={{
                      zIndex: 1,
                    }}
                    iconContainerBg="rgba(255, 92, 140, 0.07)"
                  />
                )}

                {isNoMoreSwipes && (
                  <Result
                    src={'/icons/3d-icons/sand-clock.png'}
                    title="Свайпы закончились"
                    description={
                      <>
                        Новые появятся через {swipeLeftTime}.
                        <br />
                        {activeTasksCount > 0
                          ? 'Чтобы не ждать, выполни задания или подключи Premium'
                          : 'Чтобы не ждать, подключи Premium'}
                      </>
                    }
                    style={{
                      zIndex: 1,
                    }}
                    iconContainerBg="rgba(255, 92, 140, 0.07)"
                    button={currentButtons}
                  />
                )}

                <BackButton
                  active={isShowDisclaimer}
                  visible={!!skippedProfile && !isNoMoreSwipes}
                  onClick={handleReturnSkippedProfile}
                  variant="actionSecondary"
                  style={{
                    position: 'absolute',
                    zIndex: 1,
                    top: 16,
                    left: 16,
                  }}
                />

                {isNoMoreSwipes ? null : (
                  <FiltersButton
                    filtersCount={getFiltersCount()}
                    onClick={() => navigate({ to: '/dating/filters' })}
                    variant="actionSecondary"
                    style={{
                      position: 'absolute',
                      zIndex: 1,
                      top: 16,
                      right: 16,
                    }}
                  />
                )}

                {(visibleProfiles ?? []).map((profile, index) => {
                  if ('ad' in profile) {
                    const adStyles: React.CSSProperties = {
                      position: 'absolute',
                      zIndex: index === 0 ? 99 : visibleProfiles.length - index,
                      pointerEvents: index === 0 ? 'auto' : 'none',
                      // An ad has no row: it takes the whole space on top,
                      // and under a card it must not cover that card's row.
                      ...(isSuperlikeEnabled && index > 0
                        ? { height: CARD_HEIGHT_OVER_ACTIONS }
                        : {}),
                    };

                    if (isLoadingFeedAd) {
                      return <AdvertisingCardSkeleton style={adStyles} />;
                    }

                    if (dataFeedAd) {
                      return (
                        <AdvertisingCard
                          key={`profiles-list-ad-${dataFeedAd.ad.id}`}
                          style={adStyles}
                          profile={profile}
                          data={dataFeedAd}
                          isActive={index === 0}
                          onSkip={handleSkipAdvertising}
                        />
                      );
                    }

                    return null;
                  }

                  return (
                    <ProfileCard
                      key={profile.id}
                      canSwipes={
                        !isSwipePaused &&
                        !superlikeAnimation.isPlaying &&
                        !flyOut
                      }
                      style={{
                        position: 'absolute',
                        zIndex: visibleProfiles.length - index,
                        pointerEvents: index === 0 ? 'auto' : 'none',
                        ...(isSuperlikeEnabled
                          ? { height: CARD_HEIGHT_OVER_ACTIONS }
                          : {}),
                      }}
                      profile={profile}
                      backButton={
                        <BackButton
                          active={isShowDisclaimer}
                          visible={!!skippedProfile}
                          onClick={() =>
                            auth?.user?.is_premium
                              ? handleReturnSkippedProfile()
                              : navigate({
                                  to: '/premium',
                                })
                          }
                          style={{
                            background:
                              'var(--bg-secondary-on-dark-transparent)',
                          }}
                        />
                      }
                      filtersButton={
                        <FiltersButton
                          filtersCount={getFiltersCount()}
                          onClick={() =>
                            navigate({
                              to: '/dating/filters',
                            })
                          }
                          style={{
                            background:
                              'var(--bg-secondary-on-dark-transparent)',
                          }}
                        />
                      }
                      onLike={(profile) => handleCardAnswered('like', profile)}
                      onSkip={(profile) => handleCardAnswered('pass', profile)}
                      flyOut={
                        flyOut?.id === profile.id
                          ? flyOut.action === 'like'
                            ? 'right'
                            : 'left'
                          : undefined
                      }
                      superlikeSend={superlikeSendFor(index)}
                      onDragStateChange={
                        index === 0 ? handleTopCardDrag : undefined
                      }
                    />
                  );
                })}

                {feedActionsCard && (
                  <FeedActions
                    rootRef={feedActionsRef}
                    superlikeButton={
                      hasSuperlikeHeart(feedActionsCard) && (
                        <SuperlikeButton
                          variant="row"
                          remaining={superlike.remaining}
                          canBeat={
                            !superlikeAnimation.isPlaying &&
                            !isSuperlikeSheetOpen &&
                            !flyOut
                          }
                          isPressed={
                            superlike.target?.id === feedActionsCard.id ||
                            (superlikeSentIndex === 0 &&
                              superlikeAnimation.phase === 'launch0')
                          }
                          onClick={() => superlike.open(feedActionsCard)}
                        />
                      )
                    }
                    onPass={() =>
                      handleFeedActionClick('pass', feedActionsCard)
                    }
                    onLike={() =>
                      handleFeedActionClick('like', feedActionsCard)
                    }
                  />
                )}

                {superlikeSentIndex >= 0 && (
                  <motion.div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      zIndex: 100,
                      pointerEvents: 'none',
                    }}
                    animate={
                      superlikeFxMotion
                        ? superlikeCardMotionTarget(superlikeFxMotion)
                        : undefined
                    }
                  >
                    <SuperlikeSendAnimation
                      phase={superlikeAnimation.phase}
                      launchFrom="feedActions"
                    />
                  </motion.div>
                )}
              </>
            )}
          </div>

          <div ref={ref}>
            {!isNotDating &&
              (visibleProfiles?.length ?? 0) &&
              !('ad' in visibleProfiles[0]) && (
                <ProfileInfo profile={visibleProfiles[0]} />
              )}
          </div>

          <div
            style={{
              position: 'absolute',
              top: 66 + (topOffset ?? 0),
              left: 20,
              right: 20,
              zIndex: 500,
            }}
          >
            <Disclaimer
              title="Пропущенный мэтч"
              body="Нажми назад, чтобы вернуть его"
              type="info"
              visible={isShowDisclaimer}
              style={{
                width: 'fit-content',
                margin: 'auto',
                background: 'var(--graphic-primary-on-dark-transparent)',
              }}
            />
          </div>
        </>
      )}

      <AnimatePresence>
        {isShowMatchDialog && matchProfile && (
          <MatchDialog
            matchProfile={matchProfile}
            initialMessage={matchInitialMessage}
            onClose={() => setIsShowMatchDialog(false)}
          />
        )}
      </AnimatePresence>

      {superlike.target && (
        <SuperlikeSheet
          name={superlike.target.username}
          remaining={superlike.remaining}
          message={superlike.message}
          messageError={superlike.messageError}
          isSending={superlike.isSending}
          onMessageChange={superlike.changeMessage}
          onSend={() => void superlike.send()}
          onClose={superlike.close}
        />
      )}

      {superlike.limitSheet && (
        <SuperlikeLimitSheet
          resetTime={superlike.limitSheet.resetTime}
          isPremium={superlike.isPremium}
          onPremium={superlike.openPremium}
          onClose={superlike.closeLimitSheet}
        />
      )}
    </>
  );
};

ProfilesList.displayName = 'ProfilesList';
